import React, { useState, useEffect } from 'react';
import { Button, Select } from 'flowbite-react';
import { FiPlus } from 'react-icons/fi';
import { DependencyNodeId } from 'sched';
import * as wasm from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import DependencyNodeSearchInput from './DependencyNodeSearchInput';

type DependencyDirection = 'dependencyOf' | 'dependencyBy';

interface DependencyEdgeFormProps {
  direction: DependencyDirection;
  nodeId: DependencyNodeId;
  onSuccess?: () => void;
  className?: string;
  placeholder?: string;
}

const DependencyEdgeForm: React.FC<DependencyEdgeFormProps> = ({
  direction,
  nodeId,
  onSuccess,
  className = '',
  placeholder
}) => {
  const { applyCommand, database } = useDatabase();
  const [selectedNode, setSelectedNode] = useState<DependencyNodeId | null>(null);
  const [selectedChoice, setSelectedChoice] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clearSearchTrigger, setClearSearchTrigger] = useState({});

  // Determine if selected node is a Decision and get its choices
  const isDecisionSelected = selectedNode && 'Decision' in selectedNode;
  const decisionChoices = (() => {
    if (!isDecisionSelected || !database) return [];
    const decisionId = (selectedNode as { Decision: string }).Decision;
    const decisions = database.decisions();
    const decision = decisions[decisionId];
    return decision?.choices() ?? [];
  })();

  const handleSelect = (node: DependencyNodeId, _title: string) => {
    setSelectedNode(node);
    setSelectedChoice(''); // Reset choice when selection changes
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedNode) return;

    setIsSubmitting(true);
    try {
      // Determine which node is the dependency and which is the dependent
      // based on the direction prop
      const by = direction === 'dependencyOf' ? selectedNode : nodeId;
      const on = direction === 'dependencyOf' ? nodeId : selectedNode;

      // Create a command to add the dependency
      const command: wasm.DatabaseCommand = {
        type: "SetDependency",
        dependency: {
          by,
          on,
          annotation: null,
          via_choice: selectedChoice || null
        },
        enabled: true
      };

      // Apply the command
      await applyCommand(command);

      // Reset the form
      setSelectedNode(null);
      setSelectedChoice('');
      setClearSearchTrigger({});

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error adding dependency:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine the placeholder text based on the direction or use the provided placeholder
  const getPlaceholderText = () => {
    if (placeholder) {
      return placeholder;
    } else if (direction === 'dependencyOf') {
      return "Search for a node that depends on this...";
    } else {
      return "Search for a node this depends on...";
    }
  };

  return (
    <form onSubmit={handleSubmit} className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-2">
        <div className="flex-grow">
          <DependencyNodeSearchInput
            onSelect={handleSelect}
            placeholder={getPlaceholderText()}
            clearOnChange={clearSearchTrigger}
          />
        </div>
        <Button
          type="submit"
          size="sm"
          color="blue"
          disabled={!selectedNode || isSubmitting}
        >
          <FiPlus className="h-5 w-5" />
        </Button>
      </div>

      {/* Choice selector for Decision nodes */}
      {isDecisionSelected && decisionChoices.length > 0 && (
        <div className="flex items-center gap-2 pl-1">
          <span className="text-sm text-cm-text-secondary">Gated on choice:</span>
          <Select
            sizing="sm"
            value={selectedChoice}
            onChange={(e) => setSelectedChoice(e.target.value)}
          >
            <option value="">Any choice</option>
            {decisionChoices.map((choice: string) => (
              <option key={choice} value={choice}>{choice}</option>
            ))}
          </Select>
        </div>
      )}
    </form>
  );
};

export default DependencyEdgeForm;
